import { useEffect, useState } from 'react'
import { fetchUsers } from '../../services/fetchAllUser'
import { User } from '../interfaces/user'
import { API_BASE_URL, WEBSITE_URL } from '../../services/api'
import { useTranslation } from 'react-i18next'
import { Visibility } from '@mui/icons-material'
import { FaUser } from 'react-icons/fa'
import MyButton from '../components/buttons'
import { MdAnnouncement } from 'react-icons/md'

const defaultImageUrl = (gender: string | undefined) => {
  const baseUrl = window.location.origin

  switch (gender) {
    case 'man':
      return `${baseUrl}/images/silouhette-male.jpg`
    case 'woman':
      return `${baseUrl}/images/silouhette-female.jpg`
    case 'transgender':
      return `${baseUrl}/images/silouhette-transgender.jpg`
    case 'couple':
      return `${baseUrl}/images/silouhette-couple.jpg`
    default:
      return `${baseUrl}/images/silouhette.jpg`
  }
}

const formatAnnouncePath = (user: User) => {
  if (!user.announce?.title || !user.id) return ''

  const formatGender = (gender: string) => {
    switch (gender) {
      case 'woman':
        return 'femme'
      case 'man':
        return 'homme'
      case 'transgender':
        return 'transgenre'
      case 'couple':
        return 'couple'
      default:
        return gender
    }
  }

  const formatStatus = (statuses: string[]) => {
    return statuses
      .map(status => {
        switch (status) {
          case 'libertins ou visiteur':
            return 'libertins-visiteurs'
          case 'private':
            return 'prive'
          case 'escort':
            return 'escort'
          case 'massage':
            return 'massage'
          default:
            return status
        }
      })
      .join('-')
  }

  const formatURL = (title: string, id: number) => {
    try {
      const encodedId = btoa(id.toString()).replace(/[+/=]/g, '')
      return `${title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')}-${encodedId}`
    } catch (error) {
      console.error('Error encoding URL:', error)
      return ''
    }
  }

  const gender = formatGender(user.userProfile?.gender || '')
  const status = formatStatus(user.announce.status || [])
  const slug = formatURL(user.announce.title, user.id)

  return `/announce/${gender}/${status}/${user.announce.title}/${user.announce.id}`
}

const PopularPage = () => {
  const { t } = useTranslation()
  const [cityAnnouncements, setCityAnnouncements] = useState<{
    [key: string]: User[]
  }>({})
  const [currentPage, setCurrentPage] = useState(1)
  const citiesPerPage = 10

  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        const users = await fetchUsers()

        const cityGroups = users.reduce(
          (acc: { [key: string]: User[] }, user) => {
            // Handle cases where address or city might be undefined
            const city = user.address?.city?.toLowerCase() || 'unknown'
            if (!acc[city]) {
              acc[city] = []
            }
            acc[city].push(user)
            return acc
          },
          {},
        )

        // Process each city group
        Object.keys(cityGroups).forEach(city => {
          cityGroups[city].sort(
            (a, b) => (b.announce?.views || 0) - (a.announce?.views || 0),
          )
          cityGroups[city] = cityGroups[city].slice(0, 10)
        })

        // Remove the 'unknown' city group if you don't want to display users without a city
        if (cityGroups['unknown']) {
          delete cityGroups['unknown']
        }

        const sortedCityGroups = Object.fromEntries(
          Object.entries(cityGroups).sort(
            ([, a], [, b]) => b.length - a.length,
          ),
        )

        setCityAnnouncements(sortedCityGroups)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchAndProcessData()
  }, [])

  const getImageUrl = (user: User) => {
    const fileName = user.gallery?.fileName
    return fileName
      ? `${API_BASE_URL}/images/${user.id}/${fileName}`
      : defaultImageUrl(user.userProfile?.gender)
  }

  const totalPages = Math.ceil(
    Object.keys(cityAnnouncements).length / citiesPerPage,
  )

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const currentCities = Object.entries(cityAnnouncements)
    .filter(([city, users]) => users.some(user => user.announce))
    .slice((currentPage - 1) * citiesPerPage, currentPage * citiesPerPage)

  return (
    <>
      <div
        id='premium-slider'
        className='text-center w-full align-middle bg-redpink text-white  py-3'
      >
        <p className='uppercase text-2xl'>{t('popular-announces')}</p>
        <p className=''>{t('popular-announces-description')}</p>
      </div>
      <div className='p-4 space-y-6'>
        {currentCities.map(([city, users]) => {
          const totalAnnouncements = users.reduce(
            (count, user) => count + (user.announce ? 1 : 0),
            0,
          )

          return (
            <div
              key={city}
              className='rounded-lg bg-white border-anthracite border'
            >
              <h2 className='text-lg font-semibold capitalize bg-anthracite rounded-t-md text-white py-2 px-4'>
                {city} ({totalAnnouncements} {t('announces')})
              </h2>

              <div className='p-2'>
                <div className='grid gap-2 md:grid-cols-2 lg:grid-cols-2'>
                  {users.map(
                    user =>
                      user.announce?.title && (
                        <a
                          key={user.id}
                          className='hover:bg-grisclair2 bg-grisclair p-2 rounded-lg flex items-center'
                          href={formatAnnouncePath(user)}
                        >
                          <img
                            src={getImageUrl(user)}
                            alt={user.announce?.title}
                            className='w-12 h-12 object-cover rounded mr-4'
                          />
                          <div className='flex flex-col'>
                            <h3 className='font-semibold text-base flex items-center'>
                              <MdAnnouncement className='mr-1' />{' '}
                              <span>{user.announce?.title}</span>
                            </h3>
                            <div className='flex items-center mt-1'>
                              <p className='text-sm text-redpink flex items-center mr-4'>
                                <FaUser className='mr-1' />
                                <span>
                                  {user.firstname} {user.nickname}
                                </span>
                              </p>
                              <p className='text-sm text-anthracite flex items-center'>
                                <Visibility className='mr-1' />
                                <span>{user.announce?.views || 0}</span>
                              </p>
                            </div>
                          </div>
                        </a>
                      ),
                  )}
                </div>
              </div>
            </div>
          )
        })}

        <div className='flex justify-center mt-4'>
          <MyButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            variant='anthracite-redpink'
          >
            {t('create-ad.previous')}
          </MyButton>
          <span className='px-4 py-2 mx-1'>
            {t('page')} {currentPage} / {totalPages}
          </span>
          <MyButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            variant='anthracite-redpink'
          >
            {t('create-ad.next')}
          </MyButton>
        </div>
      </div>
    </>
  )
}

export default PopularPage
